"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import {
  ShoppingBag,
  Trash2,
  Minus,
  Plus,
  ArrowRight,
  Sparkles,
  X,
} from "lucide-react";
import { useCart, type CartItem } from "./CartProvider";
import EggImage from "./EggImage";
import Badge from "./Badge";
import { fmtMxn } from "@/lib/currency";
import { track } from "@/lib/analytics";
import { ANALYTICS_EVENTS, buildGA4Item } from "@/lib/analytics-events";
import type { PublicPresentation } from "@/lib/presentations";
import {
  pickEggPriceForPresentation,
  pickPresentationPhoto,
} from "@/lib/presentations";
import type { PublicEggType } from "@/lib/egg-types";
import {
  summarizeDiscountForEgg,
  computeAveragePercentForSlots,
} from "@/lib/discounts";
import {
  fetchAptoStockBatch,
  maxCartonsUnderAptoCap,
  type AptoStock,
} from "@/lib/apto-stock";

interface Props {
  /**
   * Resolved server-side from the session cookie. Drives the primary
   * checkout CTA destination: logged-in users go to /checkout (the
   * auth-gated flow with saved addresses + credit balance), anonymous
   * visitors go to /checkout-guest (no-login form that creates a
   * guest User on submit). Default false keeps backward compatibility
   * for any callers we haven't migrated yet.
   */
  isAuthenticated?: boolean;
  /**
   * Coordenadas de la dirección predeterminada del usuario logueado (o
   * null: anónimo / sin dirección). Cuando existe, el carrito cotiza el
   * envío y lo muestra en el resumen; si no, cae a "Se calcula al pagar".
   */
  defaultAddress?: { latitude: number; longitude: number } | null;
}

/**
 * Fetch the public catalog (presentations + egg-types) through the
 * client-side BFFs. Returns empty arrays on failure — the cart still
 * works with the locally snapshotted data; we just skip the refresh.
 */
async function fetchCatalogSnapshot(): Promise<{
  presentations: PublicPresentation[];
  eggTypes: PublicEggType[];
  products: CartProductInfo[];
}> {
  const [presRes, eggRes, prodRes] = await Promise.all([
    fetch("/api/presentations/public", { cache: "no-store" }).catch(() => null),
    fetch("/api/egg-types/public", { cache: "no-store" }).catch(() => null),
    // Precio del cliente (mayoreo si aplica). Anónimo → 401 → []; sus líneas de
    // producto son locales y ya traen nombre/precio, así que no las necesita.
    fetch("/api/products/me", { cache: "no-store" }).catch(() => null),
  ]);
  const presentations: PublicPresentation[] =
    presRes && presRes.ok ? await presRes.json().catch(() => []) : [];
  const eggTypes: PublicEggType[] =
    eggRes && eggRes.ok ? await eggRes.json().catch(() => []) : [];
  const products: CartProductInfo[] =
    prodRes && prodRes.ok ? await prodRes.json().catch(() => []) : [];
  return { presentations, eggTypes, products };
}

/** Campos del producto que el carrito necesita para enriquecer sus líneas. */
interface CartProductInfo {
  id: string;
  name: string;
  price: number;
  category: string | null;
  images: string[];
  stock: number;
}

/**
 * Recompute the snapshotted line price for a uniform cart item using
 * the latest presentation + egg-type data. Mirrors the math in
 * `AddToCartButton.handleAdd`: `ceil(unitPrice × capacity + packaging
 * + label)`. Returns `null` when the catalog lookup fails so callers
 * can fall back to the local snapshot.
 */
function recomputePricePerPackage(
  item: CartItem,
  presentations: PublicPresentation[],
  eggTypes: PublicEggType[],
): number | null {
  const presentation = presentations.find((p) => p.id === item.presentationId);
  if (!presentation) return null;
  const packaging = Number(presentation.packagingCost ?? 0);
  const label = Number(presentation.labelCost ?? 0);
  const capacity = presentation.capacity;

  if (item.slots && item.slots.length > 0) {
    // Custom carton: sum per-slot effective prices (matches the
    // builder's compose path). We don't have the bare unitPrice for
    // every slot egg-type at this layer, but `pickEggPriceForPresentation`
    // falls back to it when missing, so we look each one up.
    let sumUnits = 0;
    for (const slot of item.slots) {
      const egg = eggTypes.find((e) => e.id === slot.eggTypeId);
      if (!egg) return null;
      sumUnits += pickEggPriceForPresentation(presentation, egg);
    }
    return Math.ceil(sumUnits + packaging + label);
  }

  const egg = eggTypes.find((e) => e.id === item.eggTypeId);
  if (!egg) return null;
  const perEgg = pickEggPriceForPresentation(presentation, egg);
  return Math.ceil(perEgg * capacity + packaging + label);
}

/**
 * Best-effort enrichment + price refresh for the cart items. Three
 * jobs in one pass:
 *   1. Fill `name`, `presentationLabel`, `imageUrl` for placeholder
 *      items the server returned that the client had no local copy of
 *      (cross-device login path).
 *   2. Refresh `pricePerUnit` so an item that entered an APTO promo
 *      since it was added now shows the discounted figure.
 *   3. Surface whether each line is currently on promo + carry the
 *      original price so the UI can render the strike + Oferta badge
 *      without re-running the math twice.
 */
interface EnrichedItem extends CartItem {
  /** Original (pre-discount) per-package price. Equal to `pricePerUnit`
   *  when there is no active promo for this line. */
  originalPricePerUnit: number;
  /** True when the line currently has at least one egg-type on APTO. */
  onPromo: boolean;
  /**
   * Maximum carton quantity allowed under the APTO bucket cap for any
   * egg-type referenced by the line. `Infinity` means "no cap" — the
   * line is FRESCO or APTO data hasn't loaded yet. Drives the
   * disabled-state of the `+` button and the warning copy.
   */
  maxQuantity: number;
  /**
   * Resolved units-per-carton map (egg-type → units in one carton of
   * this line). Persisted on the enriched object so the JSX cap math
   * can be unit-tested separately if needed.
   */
  unitsPerCartonByEgg: Map<string, number>;
}

function enrichItems(
  items: CartItem[],
  presentations: PublicPresentation[],
  eggTypes: PublicEggType[],
  aptoStockByEgg: ReadonlyMap<string, AptoStock>,
  products: CartProductInfo[],
): EnrichedItem[] {
  return items.map((item) => {
    // ── Línea de PRODUCTO genérico ────────────────────────────────
    // Enriquece nombre/precio/imagen del catálogo (indispensable para los
    // placeholders traídos del server en otro dispositivo). Sin huevo/APTO.
    if (item.productId) {
      const prod = products.find((p) => p.id === item.productId);
      const pricePerUnit = prod ? Number(prod.price) : item.pricePerUnit;
      return {
        ...item,
        name: prod?.name || item.name || "Producto",
        presentationLabel:
          item.presentationLabel || prod?.category || "Producto",
        imageUrl: item.imageUrl ?? prod?.images?.[0] ?? null,
        pricePerUnit,
        originalPricePerUnit: pricePerUnit,
        onPromo: false,
        saleType: undefined,
        capacity: undefined,
        // Cap por stock del producto (el backend re-valida al pagar). Sin
        // catálogo (anónimo) → sin cap local.
        maxQuantity: prod ? prod.stock : Number.POSITIVE_INFINITY,
        unitsPerCartonByEgg: new Map<string, number>(),
      };
    }
    const presentation = presentations.find(
      (p) => p.id === item.presentationId,
    );
    const egg = eggTypes.find((e) => e.id === item.eggTypeId);

    // Refreshed display fields. Prefer fresh catalog values; fall
    // back to whatever the local snapshot already carried.
    const name = item.name || egg?.name || item.name;
    const presentationLabel =
      item.presentationLabel || presentation?.name || item.presentationLabel;
    const imageUrl =
      item.imageUrl ?? (presentation
        ? pickPresentationPhoto(presentation, item.eggTypeId)
        : null);

    const refreshedPrice = recomputePricePerPackage(
      item,
      presentations,
      eggTypes,
    );
    const pricePerUnit = refreshedPrice ?? item.pricePerUnit;

    // Original-price math: same as refreshed but using the pre-promo
    // unit price map when present. For custom slots we use the
    // average APTO percentage to back out the original; for uniform
    // we look up `originalPricePerUnitByEgg` directly.
    let originalPricePerUnit = pricePerUnit;
    let onPromo = false;
    if (presentation) {
      const packaging = Number(presentation.packagingCost ?? 0);
      const label = Number(presentation.labelCost ?? 0);
      if (item.slots && item.slots.length > 0) {
        const summary = computeAveragePercentForSlots(presentation, item.slots);
        onPromo = summary.hasDiscount;
        if (onPromo && summary.maxPercent > 0 && summary.maxPercent < 1) {
          // Undo the discount on the per-egg portion of the price.
          const pureEggCost = Math.max(0, pricePerUnit - packaging - label);
          const undiscounted = pureEggCost / (1 - summary.maxPercent);
          originalPricePerUnit = Math.ceil(undiscounted + packaging + label);
        }
      } else if (egg) {
        const discount = summarizeDiscountForEgg(presentation, item.eggTypeId);
        onPromo = discount.hasDiscount;
        const originalPerEgg =
          presentation.originalPricePerUnitByEgg?.[item.eggTypeId];
        if (onPromo && typeof originalPerEgg === "number") {
          originalPricePerUnit = Math.ceil(
            originalPerEgg * presentation.capacity + packaging + label,
          );
        }
      }
    }

    // Refreshed wire-shape metadata. Server-only items arrive as
    // placeholders with conservative defaults — once the catalog is in
    // hand we authoritatively pin saleType/capacity to the live
    // presentation so the next debounced PUT projects to the correct
    // shape. Local-only items already carry these from add-time.
    const saleType: "MENUDEO" | "MAYOREO" | undefined = presentation
      ? presentation.saleType === "MENUDEO"
        ? "MENUDEO"
        : "MAYOREO"
      : item.saleType;
    const capacity = presentation?.capacity ?? item.capacity;

    // Build the units-per-carton map for THIS line so we can match it
    // against the APTO stock map. MAYOREO contributes `capacity` of the
    // single egg-type; MENUDEO contributes 1 per slot grouped by
    // egg-type. Uniform MENUDEO lines (every slot the same egg-type)
    // collapse to `{ eggTypeId → capacity }` via the loop. Lines whose
    // capacity hasn't loaded yet skip the cap entirely.
    const unitsPerCartonByEgg = new Map<string, number>();
    if (capacity && presentation) {
      if (item.slots && item.slots.length > 0) {
        for (const slot of item.slots) {
          unitsPerCartonByEgg.set(
            slot.eggTypeId,
            (unitsPerCartonByEgg.get(slot.eggTypeId) ?? 0) + 1,
          );
        }
      } else if (item.eggTypeId) {
        unitsPerCartonByEgg.set(item.eggTypeId, capacity);
      }
    }

    // Cap = floor(aptoUnits / unitsPerCarton) across every APTO
    // egg-type. Infinity when no egg-type in the line is APTO.
    const maxQuantity = maxCartonsUnderAptoCap(
      unitsPerCartonByEgg,
      aptoStockByEgg,
    );

    return {
      ...item,
      name,
      presentationLabel,
      imageUrl,
      pricePerUnit,
      originalPricePerUnit,
      onPromo,
      saleType,
      capacity,
      maxQuantity,
      unitsPerCartonByEgg,
    };
  });
}

/**
 * Smallest APTO bucket across every egg-type the line references.
 * That number is the right one to surface in the warning copy — it's
 * the bottleneck the customer needs to widen (either by reducing
 * other lines that share the same egg-type, or by waiting for fresh
 * stock to land).
 *
 * Returns 0 if the line has no APTO egg-types in the stock map (the
 * cap path shouldn't reach here in that case, but the fallback keeps
 * the JSX safe).
 */
function aptoUnitsAvailableForLine(
  item: EnrichedItem,
  aptoStockByEgg: ReadonlyMap<string, AptoStock>,
): number {
  let minUnits = Number.POSITIVE_INFINITY;
  for (const eggTypeId of item.unitsPerCartonByEgg.keys()) {
    const stock = aptoStockByEgg.get(eggTypeId);
    if (!stock || stock.currentState !== "APTO") continue;
    if (stock.aptoUnits < minUnits) minUnits = stock.aptoUnits;
  }
  return minUnits === Number.POSITIVE_INFINITY ? 0 : minUnits;
}

// Vista del carrito básica para sesión 1. La UI de checkout completa
// (selector de dirección, slot de entrega, payment) va en sesión 3.
export default function CartView({
  isAuthenticated = false,
  defaultAddress = null,
}: Props) {
  const t = useTranslations("cart");
  const {
    items,
    hydrated,
    setQuantity,
    remove,
    hydrateItems,
    cartId,
  } = useCart();
  const searchParams = useSearchParams();
  const [presentations, setPresentations] = useState<PublicPresentation[]>([]);
  const [eggTypes, setEggTypes] = useState<PublicEggType[]>([]);
  const [products, setProducts] = useState<CartProductInfo[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [recoveryDismissed, setRecoveryDismissed] = useState(false);
  // APTO stock by egg-type. Only populated for egg-types referenced by
  // the cart AND currently in APTO state (FRESCO lines have no cap, so
  // we skip the fetch to keep the round-trip small).
  const [aptoStockByEgg, setAptoStockByEgg] = useState<
    ReadonlyMap<string, AptoStock>
  >(() => new Map());

  // True when the URL carries `?recovered=1` — set by the cart
  // abandonment email link. Cleared by the dismiss button (no URL
  // rewrite — the banner just disappears for this navigation).
  const showRecoveryBanner =
    searchParams?.get("recovered") === "1" && !recoveryDismissed;

  // Pull the catalog snapshot once on mount so we can refresh prices
  // + enrich any server-returned placeholder items. Runs after cart
  // hydration to avoid a wasted fetch if the cart is empty.
  useEffect(() => {
    if (!hydrated) return;
    if (catalogLoaded) return;
    let cancelled = false;
    if (items.length === 0) {
      // Nothing to enrich yet — defer the fetch until an item lands.
      // We skip the state flip so the next item add re-runs this
      // effect cleanly (no spurious "loaded with empty catalog").
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      const snapshot = await fetchCatalogSnapshot();
      if (cancelled) return;
      setPresentations(snapshot.presentations);
      setEggTypes(snapshot.eggTypes);
      setProducts(snapshot.products);
      setCatalogLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, catalogLoaded, items.length]);

  // Compose the enriched line list whenever the cart or catalog
  // moves. Memoised so the re-render does not redo the math when
  // unrelated provider state ticks.
  const enriched = useMemo<EnrichedItem[]>(
    () => enrichItems(items, presentations, eggTypes, aptoStockByEgg, products),
    [items, presentations, eggTypes, aptoStockByEgg, products],
  );

  // Compute the egg-type ids whose presentation currently carries an
  // APTO discount. Memoised so the fetch effect only re-runs when the
  // SET of involved egg-types changes (not on every quantity tick —
  // the bucket size is independent of the carton count the customer
  // selected). Without `aptoDiscountByEgg` filtering we'd hit the BFF
  // for every FRESCO egg-type too.
  const aptoEggIdsKey = useMemo(() => {
    if (!catalogLoaded || enriched.length === 0) return "";
    const ids = new Set<string>();
    for (const item of enriched) {
      const presentation = presentations.find(
        (p) => p.id === item.presentationId,
      );
      if (!presentation?.aptoDiscountByEgg) continue;
      const map = presentation.aptoDiscountByEgg;
      if (item.slots && item.slots.length > 0) {
        for (const slot of item.slots) {
          if (map[slot.eggTypeId]) ids.add(slot.eggTypeId);
        }
      } else if (item.eggTypeId && map[item.eggTypeId]) {
        ids.add(item.eggTypeId);
      }
    }
    return Array.from(ids)
      .sort((a, b) => a.localeCompare(b))
      .join(",");
  }, [catalogLoaded, presentations, enriched]);

  // Fetch APTO stock for every egg-type involved. Effect body only
  // calls setState in the async callback (after the network round
  // trip), which doesn't trigger the cascading-render lint.
  //
  // `cartId` is forwarded so the user's OWN soft reservation is
  // excluded from the bucket subtraction. Without it, adding 15 APTO
  // would make the same user see "0 available" while looking at their
  // own 15 in cart. NULL `cartId` (guest, pre-first-PUT) is fine — the
  // API treats it as "no exclusion", which matches the customer's
  // mental model (they have no cart yet).
  useEffect(() => {
    if (!aptoEggIdsKey) {
      // Move the empty-state flush into a microtask so the effect
      // body itself doesn't call setState synchronously — the lint
      // rule treats sync setState inside effects as a smell.
      const t = setTimeout(
        () => setAptoStockByEgg((prev) => (prev.size === 0 ? prev : new Map())),
        0,
      );
      return () => clearTimeout(t);
    }
    let cancelled = false;
    void fetchAptoStockBatch(aptoEggIdsKey.split(","), cartId).then(
      (batch) => {
        if (cancelled) return;
        setAptoStockByEgg(batch);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [aptoEggIdsKey, cartId]);

  // Push enrichment results back into the provider once, when the
  // first catalog snapshot lands. That way the rest of the app
  // (Navbar count, checkout summary) sees the refreshed prices and
  // the next debounced PUT carries the latest snapshot too. We only
  // do this when the local cart actually had stale data — otherwise
  // we trigger a noisy state update for no reason.
  useEffect(() => {
    if (!catalogLoaded) return;
    if (presentations.length === 0 || eggTypes.length === 0) return;
    if (enriched.length === 0) return;
    const stale = enriched.some((e, idx) => {
      const local = items[idx];
      if (!local) return false;
      return (
        local.pricePerUnit !== e.pricePerUnit ||
        local.name !== e.name ||
        local.presentationLabel !== e.presentationLabel ||
        local.imageUrl !== e.imageUrl ||
        // Treat missing/wrong wire-shape metadata as stale so
        // server-hydrated placeholders get their saleType/capacity
        // pinned the moment the catalog snapshot lands.
        local.saleType !== e.saleType ||
        local.capacity !== e.capacity
      );
    });
    if (!stale) return;
    hydrateItems(
      enriched.map((e) => ({
        id: e.id,
        eggTypeId: e.eggTypeId,
        presentationId: e.presentationId,
        name: e.name,
        presentationLabel: e.presentationLabel,
        pricePerUnit: e.pricePerUnit,
        imageUrl: e.imageUrl,
        quantity: e.quantity,
        // Preserve saleType/capacity through the enrichment round-trip
        // so the next debounced PUT keeps the wire-shape contract
        // (uniform MENUDEO → synthesised slots, MAYOREO → eggTypeId).
        // Dropping them here would silently regress the fix.
        saleType: e.saleType,
        capacity: e.capacity,
        slots: e.slots,
      })),
    );
    // We intentionally exclude `enriched` from deps — it derives from
    // items + catalog, and including it would loop the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogLoaded, presentations.length, eggTypes.length]);

  const count = enriched.reduce((s, i) => s + i.quantity, 0);
  const totalAmount = enriched.reduce(
    (s, i) => s + i.quantity * i.pricePerUnit,
    0,
  );
  const totalOriginal = enriched.reduce(
    (s, i) => s + i.quantity * i.originalPricePerUnit,
    0,
  );
  const totalSavings = Math.max(0, totalOriginal - totalAmount);

  // Cotización de envío en el carrito para usuarios con dirección guardada
  // (mismo endpoint público que el checkout). Sin dirección (anónimo) o
  // carrito vacío → no cotiza y el resumen cae a "Se calcula al pagar".
  const [shipping, setShipping] = useState<{
    cost: number;
    covered: boolean;
    loading: boolean;
    quoted: boolean;
  }>({ cost: 0, covered: true, loading: false, quoted: false });

  useEffect(() => {
    if (!defaultAddress || totalAmount <= 0) {
      setShipping({ cost: 0, covered: true, loading: false, quoted: false });
      return;
    }
    let cancelled = false;
    setShipping((s) => ({ ...s, loading: true }));
    void (async () => {
      try {
        const r = await fetch("/api/coverage/quote", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            latitude: defaultAddress.latitude,
            longitude: defaultAddress.longitude,
            subtotal: totalAmount,
          }),
        });
        const d =
          r && r.ok
            ? ((await r.json()) as { covered: boolean; shippingCost: number })
            : null;
        if (cancelled) return;
        setShipping(
          d
            ? {
                cost: Number(d.shippingCost ?? 0),
                covered: !!d.covered,
                loading: false,
                quoted: true,
              }
            : { cost: 0, covered: true, loading: false, quoted: false },
        );
      } catch {
        if (!cancelled)
          setShipping({ cost: 0, covered: true, loading: false, quoted: false });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Deps primitivos (lat/lng) a propósito — evita re-cotizar por identidad
    // del objeto `defaultAddress` en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddress?.latitude, defaultAddress?.longitude, totalAmount]);

  const shippingCost = shipping.covered ? shipping.cost : 0;
  const displayedTotal = totalAmount + shippingCost;

  // GA4 `view_cart` — fired once per page-load when the cart has
  // items. Needed for the GA4 funnel report (view_cart → begin_checkout
  // → purchase) to pick up the cart step. Without it the official
  // Monetization funnel skips straight from add_to_cart to begin_checkout
  // and we lose visibility on the "saw the cart but bailed before
  // clicking checkout" drop-off — historically that's been the largest
  // single bucket of abandonment.
  // useRef (not useMemo with a mutable object) — React 19's lint
  // `react-hooks/immutability` forbids mutating a useMemo return.
  const trackedViewRef = useRef(false);
  useEffect(() => {
    if (!hydrated) return;
    if (enriched.length === 0) return;
    if (trackedViewRef.current) return;
    trackedViewRef.current = true;
    track(ANALYTICS_EVENTS.VIEW_CART, {
      currency: "MXN",
      value: totalAmount,
      items: enriched.map((it) =>
        buildGA4Item({
          id: it.presentationId,
          name: it.name || it.presentationId,
          variant: it.presentationLabel,
          price: it.pricePerUnit,
          quantity: it.quantity,
        }),
      ),
    });
  }, [hydrated, enriched, totalAmount, trackedViewRef]);

  // GA4 `cart_recovered` (custom) — fires once when the recovery banner
  // becomes visible (i.e., the customer clicked the abandonment email
  // and `?recovered=1` is in the URL). Together with the UTM params on
  // the email link this gives us: emails_sent (from API) →
  // cart_recovered (GA4) → begin_checkout → purchase, which is the
  // funnel we need to compute recovery-email effectiveness without
  // having to instrument backend correlation between email send + order
  // attribution.
  const trackedRecoveryRef = useRef(false);
  useEffect(() => {
    if (!showRecoveryBanner) return;
    if (trackedRecoveryRef.current) return;
    trackedRecoveryRef.current = true;
    track(ANALYTICS_EVENTS.CART_RECOVERED, {
      itemsCount: enriched.length,
      value: totalAmount,
      currency: "MXN",
    });
  }, [showRecoveryBanner, enriched.length, totalAmount, trackedRecoveryRef]);

  /**
   * Wraps the provider's `remove` so we can emit `remove_from_cart`
   * with the GA4 `items[]` shape BEFORE the line vanishes. Doing it
   * after the state update would lose the egg-type + presentation
   * context needed for the GA4 Monetization report to attribute the
   * removal back to the catalog row.
   */
  const handleRemove = (item: EnrichedItem) => {
    track(ANALYTICS_EVENTS.REMOVE_FROM_CART, {
      currency: "MXN",
      value: item.pricePerUnit * item.quantity,
      items: [
        buildGA4Item({
          id: item.presentationId,
          name: item.name || item.presentationId,
          variant: item.presentationLabel,
          price: item.pricePerUnit,
          quantity: item.quantity,
        }),
      ],
    });
    remove(item.id);
  };

  // Show a skeleton instead of the empty-cart message while the
  // cart is still hydrating. SSR renders with `hydrated=false` (no
  // localStorage access on the server). Without this guard, the user
  // sees "Carrito vacío" flash for ~50-200ms even when there are
  // items — the classic SSR vs client-state hydration mismatch.
  // For authenticated users, a brief secondary flash can still occur
  // between localStorage hydrate and the GET /v1/cart response, but
  // it's much shorter (~50ms) and the merge usually preserves the
  // local items so visually nothing pops in.
  if (!hydrated) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-24 rounded-xl bg-shell/60 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (enriched.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <ShoppingBag size={48} className="mx-auto text-dark/20 mb-4" />
        <h1 className="text-2xl font-black text-dark">{t("emptyTitle")}</h1>
        <p className="text-dark/60 mt-2 mb-6">{t("emptyBody")}</p>
        <Link
          href="/catalog"
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-5 py-2.5 rounded-xl transition-colors"
        >
          {t("goToCatalog")}
          <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {showRecoveryBanner && (
        <div className="mb-6 flex items-start gap-3 bg-yolk/10 border border-yolk/40 rounded-2xl px-4 py-3">
          <div className="flex-shrink-0 mt-0.5">
            <Sparkles size={18} className="text-yolk-dark" />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-bold text-dark">{t("recoveryTitle")}</p>
            <p className="text-dark/70">{t("recoveryBody")}</p>
          </div>
          <button
            type="button"
            onClick={() => setRecoveryDismissed(true)}
            aria-label={t("recoveryDismiss")}
            className="p-1 text-dark/50 hover:text-dark hover:bg-dark/5 rounded-md"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <h1 className="text-3xl font-black text-dark mb-2">{t("title")}</h1>
      <p className="text-dark/60 text-sm mb-8">
        {t("itemCount", { count })}
      </p>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="bg-white rounded-2xl border border-muted/20 divide-y divide-muted/15">
          {enriched.map((item) => (
            <div
              key={item.id}
              className="p-4 flex items-center gap-4 flex-wrap sm:flex-nowrap"
            >
              <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-shell flex-shrink-0">
                <EggImage src={item.imageUrl} alt={item.name} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-dark truncate flex items-center gap-2">
                  {item.name}
                  {item.onPromo && (
                    <Badge variant="promo" size="sm">
                      {t("promoBadge")}
                    </Badge>
                  )}
                </h3>
                <p className="text-xs text-dark/50 flex items-baseline gap-1">
                  {item.onPromo &&
                  item.originalPricePerUnit > item.pricePerUnit ? (
                    <>
                      <span className="line-through text-dark/40">
                        {fmtMxn(item.originalPricePerUnit)}
                      </span>
                      <span className="font-semibold text-success">
                        {fmtMxn(item.pricePerUnit)}
                      </span>
                    </>
                  ) : (
                    <span>{fmtMxn(item.pricePerUnit)}</span>
                  )}
                  <span> {item.presentationLabel}</span>
                </p>
                {/* APTO cap warning. Shown only when the line is on
                    promo AND the customer has hit the cap (or would
                    exceed it with one more click). The number we
                    surface is the minimum bucket size across every
                    APTO egg-type the line touches — matches what the
                    customer sees on the catalog card per egg-type. */}
                {item.onPromo &&
                  item.maxQuantity !== Number.POSITIVE_INFINITY &&
                  item.quantity >= item.maxQuantity && (
                    <p className="mt-1.5 text-[11px] text-yolk-dark font-semibold flex items-start gap-1 leading-tight">
                      <Sparkles size={11} className="mt-px flex-shrink-0" />
                      <span>
                        {t("aptoLimitReached", {
                          count: aptoUnitsAvailableForLine(item, aptoStockByEgg),
                        })}
                        <span className="block text-dark/50 font-normal">
                          {t("aptoLimitHint")}
                        </span>
                      </span>
                    </p>
                  )}
              </div>
              <div className="flex items-center gap-2 bg-shell rounded-lg p-1">
                <button
                  type="button"
                  onClick={() =>
                    setQuantity(item.id, Math.max(1, item.quantity - 1))
                  }
                  aria-label={t("decrease")}
                  className="w-7 h-7 rounded-md hover:bg-white text-dark/70 flex items-center justify-center"
                >
                  <Minus size={12} />
                </button>
                <span className="w-8 text-center text-sm font-bold">
                  {item.quantity}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Cap the increment at the APTO bucket size — going
                    // past it would force the customer to pay APTO
                    // price for FRESCO eggs once the bucket drains.
                    // Infinity means "no cap" (FRESCO line or stock
                    // not loaded yet).
                    const next = item.quantity + 1;
                    if (next > item.maxQuantity) return;
                    setQuantity(item.id, next);
                  }}
                  aria-label={t("increase")}
                  disabled={item.quantity >= item.maxQuantity}
                  className="w-7 h-7 rounded-md hover:bg-white text-dark/70 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <Plus size={12} />
                </button>
              </div>
              <p className="font-bold text-dark min-w-[80px] text-right">
                {fmtMxn(item.pricePerUnit * item.quantity)}
              </p>
              <button
                type="button"
                onClick={() => handleRemove(item)}
                aria-label={t("removeItem")}
                className="p-2 text-alert hover:bg-alert/10 rounded-md"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <aside className="bg-white rounded-2xl border border-muted/20 p-5 h-fit sticky top-20">
          <h2 className="font-bold text-dark mb-4">{t("summary")}</h2>
          <div className="flex justify-between text-sm text-dark/70 mb-2">
            <span>{t("subtotal")}</span>
            <span className="font-semibold">{fmtMxn(totalAmount)}</span>
          </div>
          {totalSavings > 0 && (
            <div className="flex justify-between text-sm text-success mb-2">
              <span className="flex items-center gap-1">
                <Sparkles size={12} />
                {t("promoBadge")}
              </span>
              <span className="font-semibold">−{fmtMxn(totalSavings)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-dark/70 mb-4">
            <span>{t("delivery")}</span>
            <span
              className={
                shipping.quoted && shipping.covered
                  ? "font-semibold text-dark"
                  : "font-semibold text-dark/40"
              }
            >
              {shipping.loading
                ? t("calculating")
                : shipping.quoted
                  ? !shipping.covered
                    ? t("outOfCoverage")
                    : shippingCost > 0
                      ? fmtMxn(shippingCost)
                      : t("freeShipping")
                  : t("deliveryAtCheckout")}
            </span>
          </div>
          <div className="border-t border-muted/20 pt-4 flex justify-between items-baseline">
            <span className="font-bold text-dark">{t("total")}</span>
            <span className="text-2xl font-black text-primary">
              {fmtMxn(displayedTotal)}
            </span>
          </div>
          {/* Single CTA — destino depende de la sesión. Antes
              "Ir a pagar" siempre apuntaba a /checkout, que el
              middleware redirige a login. Para visitantes anónimos
              eso era un detour innecesario (terminaban perdidos en
              el landing). Ahora si no hay sesión vamos directo al
              guest checkout que no requiere login. Eliminó la
              necesidad del botón secundario "Continuar como invitado"
              que solo confundía. */}
          <Link
            href={isAuthenticated ? "/checkout" : "/checkout-guest"}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-5 py-3 rounded-xl transition-colors"
          >
            {t("goToCheckout")}
            <ArrowRight size={16} />
          </Link>
          {!isAuthenticated && (
            <p className="text-[11px] text-dark/50 text-center mt-2">
              Sin crear cuenta. Solo email + nombre + dirección.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
